import { useMemo } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, CircleMarker } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, Activity } from "lucide-react";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
    iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
    shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const ambIcon = L.divIcon({
    className: "",
    html: `<div style="background:#dc2626;width:34px;height:34px;border-radius:50%;border:3px solid white;box-shadow:0 2px 10px rgba(0,0,0,0.45);display:flex;align-items:center;justify-content:center;color:white;font-weight:bold;font-size:14px;font-family:IBM Plex Mono;">A</div>`,
    iconSize: [34, 34],
    iconAnchor: [17, 17],
});

const originIcon = L.divIcon({
    className: "",
    html: `<div style="background:#10b981;width:18px;height:18px;border-radius:50%;border:3px solid white;box-shadow:0 1px 6px rgba(0,0,0,0.35);"></div>`,
    iconSize: [18, 18],
    iconAnchor: [9, 9],
});

const destIcon = L.divIcon({
    className: "",
    html: `<div style="background:#0f172a;width:18px;height:18px;border-radius:2px;border:3px solid white;box-shadow:0 1px 6px rgba(0,0,0,0.35);"></div>`,
    iconSize: [18, 18],
    iconAnchor: [9, 9],
});

/**
 * ServiceMap — visualiza el trayecto completo del servicio en Leaflet.
 * - Origen (verde), Destino (negro)
 * - Posición actual de la unidad (rojo, círculo grande con "A")
 * - Puntos GPS capturados en cada cambio de estatus (círculos azules con tooltip)
 * - Polilínea conectando origen → puntos GPS → unidad
 *
 * Props:
 *   service: { origin_lat, origin_lng, destination_lat, destination_lng, origin_address, destination_address, status_history }
 *   unit: { lat, lng, economic_number, ambulance_type } | null
 *   height: number (px)
 */
export default function ServiceMap({ service, unit, height = 320 }) {
    const trail = useMemo(() => {
        const pts = [];
        // 1) status_history GPS points (cambios de estatus)
        for (const h of service.status_history || []) {
            if (typeof h.lat === "number" && typeof h.lng === "number") {
                pts.push({
                    lat: h.lat,
                    lng: h.lng,
                    status: h.status,
                    at: h.at,
                    by_name: h.by_name,
                });
            }
        }
        return pts;
    }, [service.status_history]);

    // gps_pings = stream continuo del paramédico durante el servicio (cada 30s)
    const gpsTrail = useMemo(() => {
        return (service.gps_pings || [])
            .filter((p) => typeof p.lat === "number" && typeof p.lng === "number")
            .map((p) => [p.lat, p.lng]);
    }, [service.gps_pings]);

    const allCoords = useMemo(() => {
        const c = [];
        if (service.origin_lat && service.origin_lng) c.push([service.origin_lat, service.origin_lng]);
        trail.forEach((p) => c.push([p.lat, p.lng]));
        gpsTrail.forEach((p) => c.push(p));
        if (unit?.lat && unit?.lng) c.push([unit.lat, unit.lng]);
        if (service.destination_lat && service.destination_lng) c.push([service.destination_lat, service.destination_lng]);
        return c;
    }, [service, unit, trail, gpsTrail]);

    if (allCoords.length === 0) {
        return (
            <div className="fm-card overflow-hidden" data-testid="service-map">
                <div className="p-4 border-b border-slate-200">
                    <h3 className="font-display font-bold text-base flex items-center gap-2">
                        <MapPin className="w-4 h-4" strokeWidth={1.5} /> Trayecto del servicio
                    </h3>
                </div>
                <div className="p-10 text-center text-sm text-slate-500" style={{ minHeight: `${height}px` }} data-testid="service-map-empty">
                    <Activity className="w-8 h-8 mx-auto mb-2 text-slate-300" strokeWidth={1} />
                    Sin ubicaciones registradas todavía.
                </div>
            </div>
        );
    }

    // Bounds para hacer fit
    const lats = allCoords.map((c) => c[0]);
    const lngs = allCoords.map((c) => c[1]);
    const bounds = [[Math.min(...lats), Math.min(...lngs)], [Math.max(...lats), Math.max(...lngs)]];

    // Polilínea = origen -> trail GPS -> unidad
    const polyPoints = [];
    if (service.origin_lat && service.origin_lng) polyPoints.push([service.origin_lat, service.origin_lng]);
    trail.forEach((p) => polyPoints.push([p.lat, p.lng]));
    if (unit?.lat && unit?.lng) polyPoints.push([unit.lat, unit.lng]);

    return (
        <div className="fm-card overflow-hidden" data-testid="service-map">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
                <h3 className="font-display font-bold text-base flex items-center gap-2">
                    <MapPin className="w-4 h-4" strokeWidth={1.5} /> Trayecto del servicio
                </h3>
                <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest font-bold text-slate-500 flex-wrap">
                    <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-emerald-500 rounded-full"></span>Origen</span>
                    <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-blue-500 rounded-full"></span>GPS en vivo ({gpsTrail.length})</span>
                    <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-red-600 rounded-full"></span>Unidad</span>
                    <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-slate-900 rounded-sm"></span>Destino</span>
                </div>
            </div>
            <div style={{ height: `${height}px` }}>
                <MapContainer bounds={bounds} boundsOptions={{ padding: [40, 40] }} className="h-full w-full">
                    <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='&copy; OpenStreetMap'
                    />

                    {/* Origen */}
                    {service.origin_lat && service.origin_lng && (
                        <Marker position={[service.origin_lat, service.origin_lng]} icon={originIcon}>
                            <Popup>
                                <strong>Origen</strong>
                                <br />{service.origin_address}
                            </Popup>
                        </Marker>
                    )}

                    {/* Destino */}
                    {service.destination_lat && service.destination_lng && (
                        <Marker position={[service.destination_lat, service.destination_lng]} icon={destIcon}>
                            <Popup>
                                <strong>Destino</strong>
                                <br />{service.destination_address}
                            </Popup>
                        </Marker>
                    )}

                    {/* Trail GPS por status_history */}
                    {trail.map((p, i) => (
                        <CircleMarker
                            key={i}
                            center={[p.lat, p.lng]}
                            radius={6}
                            pathOptions={{ color: "#3b82f6", fillColor: "#3b82f6", fillOpacity: 0.8, weight: 2 }}
                        >
                            <Popup>
                                <strong>{p.status?.replace(/_/g, " ")}</strong>
                                <br />{new Date(p.at).toLocaleString()}
                                <br /><small>por {p.by_name || "Sistema"}</small>
                            </Popup>
                        </CircleMarker>
                    ))}

                    {/* Unidad actual */}
                    {unit?.lat && unit?.lng && (
                        <Marker position={[unit.lat, unit.lng]} icon={ambIcon}>
                            <Popup>
                                <strong>{unit.economic_number}</strong>
                                <br />{unit.ambulance_type}
                                <br /><small>Posición actual</small>
                            </Popup>
                        </Marker>
                    )}

                    {/* Polilínea ruta real (status_history) */}
                    {polyPoints.length >= 2 && (
                        <Polyline
                            positions={polyPoints}
                            pathOptions={{ color: "#dc2626", weight: 3, opacity: 0.7, dashArray: "8 6" }}
                        />
                    )}

                    {/* Polilínea continua de pings GPS (tracking en vivo) */}
                    {gpsTrail.length >= 2 && (
                        <Polyline
                            positions={gpsTrail}
                            pathOptions={{ color: "#3b82f6", weight: 4, opacity: 0.85 }}
                        />
                    )}
                </MapContainer>
            </div>
        </div>
    );
}
